import React from "react";
import MainLayout from "../layout/MainLayout";
import { motion } from "framer-motion";
import {
  Leaf,
  Handshake,
  Globe2,
  BatteryCharging,
  Users,
  ShieldCheck,
} from "lucide-react";
import AboutImg from "../assets/drone1.jpg";
import { Link } from "react-router-dom";

export const AboutUs = () => {
  return (
    <MainLayout>
      {/* Hero Section */}
      <section className="relative h-[65vh] rounded-3xl mx-4 sm:mx-10 mb-16 overflow-hidden shadow-xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${AboutImg})` }}
        />
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-center p-6 sm:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white max-w-3xl"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 just-me-again-down-here-regular">
              Driven by Excellence, Guided by Purpose.
            </h1>
            <p className="text-lg sm:text-xl text-white/90">
              We’re redefining mobility across Uganda — one purposeful journey at a time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="max-w-5xl mx-auto px-6 mb-24">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl sm:text-5xl font-bold mb-8 text-center just-me-again-down-here-regular"
        >
          Who We Are
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6 text-gray-700 dark:text-gray-300 text-lg leading-relaxed max-w-3xl mx-auto"
        >
          <p>
            <strong>Muhaad</strong> is Uganda’s trusted car hire and travel partner – proudly local,
            eco-conscious, and built to serve both explorers and professionals.
          </p>
          <p>
            We’re not just a car rental company – we’re on a mission to{" "}
            <strong>redefine transport</strong> in <strong>East Africa</strong>. Whether you're
            visiting Uganda’s national parks, heading to a business conference, or coordinating NGO
            fieldwork, we provide <strong>reliable, flexible, and eco-friendly transport</strong>{" "}
            solutions tailored to your journey.
          </p>
          <p>
            Our fleet includes everything from daily hire vehicles to our{" "}
            <strong>signature hybrid drone van</strong> – a one-of-a-kind travel and delivery
            vehicle offering comfort, power access, ambient lighting, and reduced emissions. It's
            ideal for long-distance travel, cross-border trips, and sustainable logistics.
          </p>
          <div>
            <p className="font-semibold mb-2">At Muhaad, we make mobility simple and seamless:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Book your ride online</li>
              <li>Choose self-drive or chauffeur-driven options</li>
              <li>Get picked up, dropped off, and supported – on your schedule</li>
            </ul>
          </div>
          <p>
            We're here to help you <strong>move smarter</strong>, travel better, and{" "}
            <strong>explore Uganda responsibly</strong>.
          </p>
        </motion.div>
      </section>

      {/* Mission Section */}
      <section className="max-w-5xl mx-auto px-6 mb-24 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl sm:text-5xl font-bold mb-6 just-me-again-down-here-regular"
        >
          Our Mission
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed max-w-3xl mx-auto"
        >
          At <strong>Muhaad</strong>, our mission is to elevate the standard of car hire and travel
          in Uganda by offering eco-conscious, dependable, and customer-focused transport
          solutions. We are committed to supporting sustainable tourism, efficient field operations,
          and seamless mobility for individuals, companies, organizations, and explorers across
          East Africa.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mt-6 max-w-3xl mx-auto"
        >
          We believe that moving from place to place should be more than just transport – it should
          be <strong>safe, stylish, purposeful</strong>, and built around your needs.
        </motion.p>
      </section>

      {/* What Sets Us Apart */}
      <section className="max-w-6xl mx-auto px-6 mb-24">
        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl sm:text-5xl font-bold text-center mb-14 just-me-again-down-here-regular"
        >
          What Sets Us Apart
        </motion.h3>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            {
              icon: <Leaf className="w-8 h-8 text-teal-600" />,
              title: "🚐 Eco-Conscious Travel, Redefined",
              desc: "Our hybrid van offers a unique combination of comfort and sustainability – with sockets, interior lighting, and a lower carbon footprint.",
            },
            {
              icon: <Handshake className="w-8 h-8 text-teal-600" />,
              title: "👔 Professionalism You Can Count On",
              desc: "Our drivers are trained, punctual, and committed to delivering an exceptional client experience.",
            },
            {
              icon: <Globe2 className="w-8 h-8 text-teal-600" />,
              title: "🌍 Trusted by NGOs & Institutions",
              desc: "FHI360, SPARK Microgrants, Finding XY and leading hotels trust us with sensitive field logistics.",
            },
            {
              icon: <BatteryCharging className="w-8 h-8 text-teal-600" />,
              title: "🛠️ Custom Solutions for Every Journey",
              desc: "We offer flexible packages for tourists, field workers, and corporate clients alike.",
            },
            {
              icon: <Users className="w-8 h-8 text-teal-600" />,
              title: "🇺🇬 100% Ugandan-Owned & Operated",
              desc: "We’re proud to be locally rooted and community-focused. Every trip supports Ugandan jobs and innovation.",
            },
            {
              icon: <ShieldCheck className="w-8 h-8 text-teal-600" />,
              title: "Reliability & Safety First",
              desc: "We prioritize security, comfort, and operational excellence — so you can travel with confidence.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white/30 dark:bg-white/10 backdrop-blur-lg p-6 rounded-xl border border-white/20 dark:border-white/10 shadow-md hover:shadow-lg hover:bg-white/40 transition"
            >
              <div className="mb-4 flex justify-center">{item.icon}</div>
              <h4 className="font-semibold text-lg mb-2 text-center group-hover:text-teal-600 transition">
                {item.title}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center max-w-4xl mx-auto px-6 mb-20">
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold mb-4 just-me-again-down-here-regular"
        >
          We're here to help you move smarter, travel better, and explore Uganda responsibly.
        </motion.h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Whether it’s daily commuting, airport pickups, or executive travel, we’ve got the perfect
          ride for you.
        </p>
        <Link
          to="/contact"
          className="inline-block px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition"
        >
          Book a Car
        </Link>
      </section>
    </MainLayout>
  );
};

export default AboutUs;
